import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useProgress } from '../../contexts/ProgressContext';
import { useNavigate } from 'react-router-dom';
import ProgressBar from '../../components/ProgressBar';
import SectionCheckbox from '../../components/SectionCheckbox';
import Quiz from '../../components/Quiz';
import { PromptBox } from '../../components/CopyButton';
import PromptPlayground from '../../components/PromptPlayground';
import StepByStepGuide from '../../components/StepByStepGuide';
import TelegramWidget from '../../components/TelegramWidget';
import '../Dashboard.css';
import '../modules/Module.css';
import './Track.css';

const MODULE_ID = 3; // Using module 3 for Claude Code progress

// Quiz questions combinées
const quizQuestions = [
  {
    question: "Quelle est la principale différence entre ChatGPT et Claude Code ?",
    options: [
      "Il n'y a aucune différence",
      "Claude Code peut créer et modifier des fichiers sur votre ordinateur",
      "ChatGPT est plus intelligent",
      "Claude Code ne parle que anglais"
    ],
    correctAnswer: 1,
    explanation: "Claude Code peut interagir avec votre système de fichiers : créer, modifier, supprimer des fichiers et dossiers. C'est un véritable assistant de développement."
  },
  {
    question: "Qu'est-ce qu'un MVP (Minimum Viable Product) ?",
    options: [
      "La version finale parfaite d'un produit",
      "La version la plus simple qui apporte de la valeur",
      "Un prototype qui ne fonctionne pas",
      "Une maquette Figma interactive"
    ],
    correctAnswer: 1,
    explanation: "Le MVP est la version la plus simple de votre produit qui apporte déjà de la valeur. L'objectif est de lancer vite, tester avec de vrais utilisateurs, puis itérer."
  },
  {
    question: "Combien de fonctionnalités un MVP doit-il avoir ?",
    options: [
      "Le plus possible",
      "10-15 minimum",
      "3-5 essentielles",
      "Aucune"
    ],
    correctAnswer: 2,
    explanation: "Un MVP doit avoir 3-5 fonctionnalités essentielles maximum. Focus sur ce qui résout vraiment le problème de l'utilisateur."
  },
  {
    question: "Quel service est recommandé pour déployer une app React ?",
    options: [
      "Un serveur dédié OVH",
      "Vercel",
      "FTP classique",
      "CD-ROM"
    ],
    correctAnswer: 1,
    explanation: "Vercel offre un déploiement automatique depuis GitHub, un certificat SSL gratuit, et un CDN mondial. Parfait pour les apps React/Next.js."
  },
  {
    question: "Quel est le tarif de référence pour un site web créé avec l'IA ?",
    options: [
      "Gratuit",
      "50-100€",
      "500-2000€",
      "10 000€ minimum"
    ],
    correctAnswer: 2,
    explanation: "Un site web professionnel créé avec l'IA peut se vendre entre 500€ et 2000€. Vos clients paient pour le résultat, pas pour le temps passé."
  }
];

// Playground pour brief de projet
const projectVariables = [
  {
    name: 'project_name',
    label: 'Nom du projet',
    type: 'text',
    placeholder: 'Ex: InvoiceMaster, FitCoach, TaskFlow...'
  },
  {
    name: 'problem',
    label: 'Problème résolu',
    type: 'textarea',
    placeholder: 'Ex: Les freelances perdent 2h par semaine à créer leurs factures manuellement...'
  },
  {
    name: 'features',
    label: 'Fonctionnalités MVP (3-5)',
    type: 'textarea',
    placeholder: 'Ex:\n1. Créer une facture en 30 secondes\n2. Sauvegarder les clients\n3. Export PDF\n4. Envoyer par email'
  },
  {
    name: 'design',
    label: 'Style visuel',
    type: 'select',
    options: ['Moderne minimaliste', 'Corporate professionnel', 'Fun et coloré', 'Luxueux et premium', 'Tech futuriste']
  }
];

const projectTemplate = `Crée une application web complète : {{project_name}}

## Problème résolu
{{problem}}

## Fonctionnalités MVP
{{features}}

## Design
Style : {{design}}

---

## Instructions pour Claude Code

1. **Architecture**
   - Application React avec composants réutilisables
   - Routing pour la navigation entre pages
   - State management (Context ou useState)

2. **Pages à créer**
   - Landing page avec proposition de valeur
   - Page principale (fonctionnalité core)
   - Page de paramètres si pertinent

3. **Technique**
   - Responsive design (mobile-first)
   - Stockage local (localStorage)
   - Design system cohérent

4. **UX**
   - Feedback utilisateur clair
   - Loading states
   - Messages d'erreur explicites

Commence par créer la structure du projet et la page principale.`;

// Guide pour créer une app
const appGuideSteps = [
  {
    title: "Définir le problème et la cible",
    content: "Avant de coder, clarifiez : Quel problème résolvez-vous ? Pour qui ? Pourquoi les solutions existantes ne suffisent pas ?",
    tips: ["Si vous ne pouvez pas l'expliquer en 1 phrase, c'est trop complexe", "Parlez à 5 personnes de votre cible avant de coder"]
  },
  {
    title: "Lister les fonctionnalités MVP",
    content: "Identifiez 3-5 fonctionnalités absolument nécessaires. Tout le reste attendra la V2.",
    code: `MVP - À faire :
✅ Fonctionnalité core (LA plus importante)
✅ Onboarding simple
✅ Sauvegarde des données

V2 - Plus tard :
⏳ Multi-utilisateurs
⏳ Intégrations tierces
⏳ Analytics avancés`,
    checkpoint: "Vous devez pouvoir lister vos features en 30 secondes"
  },
  {
    title: "Créer le brief pour Claude Code",
    content: "Utilisez le template ci-dessus pour rédiger un brief détaillé. Plus vous êtes précis, meilleur sera le résultat.",
    prompt: `Crée une application [NOM] en React.

FONCTIONNALITÉ PRINCIPALE :
[Description en 2-3 phrases]

PARCOURS UTILISATEUR :
1. L'utilisateur arrive et voit...
2. Il clique sur...
3. Il obtient...

DESIGN : [style + couleur principale]`
  },
  {
    title: "Développer avec Claude Code",
    content: "Lancez Claude Code, donnez-lui votre brief. Laissez-le créer la structure, puis guidez-le feature par feature.",
    tips: ["Testez après chaque feature", "Demandez des explications si vous ne comprenez pas", "Sauvegardez régulièrement"]
  },
  {
    title: "Déployer sur Vercel",
    content: "Poussez votre code sur GitHub, connectez-le à Vercel, et votre app est en ligne en 2 minutes.",
    code: `# Pousser sur GitHub
git add .
git commit -m "MVP ready"
git push

# Sur Vercel
1. Import project from GitHub
2. Deploy
3. Votre app est en ligne !`,
    checkpoint: "Votre app est accessible via une URL publique"
  }
];

const ClaudeCodeTrack = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { progress } = useProgress();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState({
    intro: true,
    installation: false,
    launch: false,
    powers: false,
    pricing: false,
    firstproject: false,
    exercise: false,
    briefs: false,
    mvp: false,
    deploy: false,
    business: false,
    faq: false
  });

  if (!isAuthenticated) { navigate('/login'); return null; }

  const handleLogout = () => { logout(); navigate('/'); };
  const toggleSection = (section) => setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  const totalProgress = progress?.totalProgress || 0;

  return (
    <div className="dashboard-layout">
      {/* SIDEBAR PRO */}
      <aside className="dashboard-sidebar">
        <div className="sidebar-logo" onClick={() => navigate('/dashboard')}>
          <span className="logo-text">F12IA</span>
          <span className="logo-tm">™</span>
        </div>

        <div className="sidebar-user">
          <div className="user-avatar">{user?.firstName?.charAt(0).toUpperCase() || 'U'}</div>
          <div className="user-info">
            <span className="user-name">{user?.firstName || 'Utilisateur'}</span>
            <span className="user-role">Membre</span>
          </div>
        </div>

        <nav className="sidebar-nav-pro">
          <div className="nav-label">MENU</div>
          <div className="nav-item" onClick={() => navigate('/dashboard')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg></span>
            <span className="nav-text">Tableau de bord</span>
          </div>

          <div className="nav-label">FORMATIONS</div>
          <div className="nav-item" onClick={() => navigate('/formation/chatgpt')}>
            <span className="nav-icon chatgpt-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729z"/></svg></span>
            <span className="nav-text">ChatGPT</span>
          </div>
          <div className="nav-item active" onClick={() => navigate('/formation/claude')}>
            <span className="nav-icon claude-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M4.709 15.955l4.72-2.647.08-.23-.08-.128-2.251-3.614-.144-.072-4.794.893-.071.158.503 2.332.014.13 2.023 3.178z"/></svg></span>
            <span className="nav-text">Claude Code</span>
          </div>
          <div className="nav-item fusion-nav" onClick={() => navigate('/formation/fusion')}>
            <span className="nav-icon fusion-icon"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M13 10V3L4 14h7v7l9-11h-7z"/></svg></span>
            <span className="nav-text">Fusion</span>
            <span className="nav-badge-pro">PRO</span>
          </div>

          <div className="nav-label">COMPTE</div>
          <div className="nav-item" onClick={() => navigate('/profile')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 4-6 8-6s8 2 8 6"/></svg></span>
            <span className="nav-text">Mon profil</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/resources')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg></span>
            <span className="nav-text">Ressources</span>
          </div>
          <div className="nav-item" onClick={() => navigate('/certificate')}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="8" r="6"/><path d="M8 14l-2 8 6-3 6 3-2-8"/></svg></span>
            <span className="nav-text">Certificat</span>
          </div>
        </nav>

        <div className="sidebar-progress-pro">
          <div className="progress-header">
            <span className="progress-title">Progression</span>
            <span className="progress-value">{Math.round(totalProgress)}%</span>
          </div>
          <div className="progress-bar-pro">
            <div className="progress-fill-pro" style={{ width: `${totalProgress}%` }} />
          </div>
        </div>

        <div className="sidebar-bottom">
          <div className="nav-item logout-item" onClick={handleLogout}>
            <span className="nav-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg></span>
            <span className="nav-text">Déconnexion</span>
          </div>
        </div>
      </aside>

      <main className="dashboard-main">
        <div className="module-header track-header claude-track">
          <button className="back-btn" onClick={() => navigate('/dashboard')}>← Retour au Dashboard</button>
          <div className="track-badge claude">FORMATION COMPLÈTE</div>
          <h1>💻 Maîtriser Claude Code de A à Z</h1>
          <p className="module-subtitle">Créez des sites web et applications sans savoir coder</p>
          <ProgressBar moduleId={MODULE_ID} />
        </div>

        <div className="module-content">

          {/* ==================== SECTION 1: C'EST QUOI CLAUDE CODE ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('intro')}>
              <div className="section-toggle-content">
                <span className="section-number">01</span>
                <div>
                  <h2>C'est quoi Claude Code ?</h2>
                  <p>Comprendre en 2 minutes l'outil qui crée des apps à ta place</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.intro ? '▼' : '▶'}</span>
            </div>

            {expandedSections.intro && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Comprendre Claude Code simplement</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="intro" />
                </div>

                {/* DÉFINITION ULTRA SIMPLE */}
                <div className="highlight-box gold">
                  <strong>🎯 C'est quoi Claude Code ?</strong><br/><br/>
                  <span style={{fontSize: '1.1rem'}}>
                    Claude Code, c'est comme avoir un <strong>développeur professionnel</strong> qui travaille sur ton ordinateur.<br/><br/>
                    👉 Tu lui dis ce que tu veux (en français)<br/>
                    👉 Il crée le site web ou l'application pour toi<br/>
                    👉 Tu n'as PAS besoin de savoir coder !
                  </span>
                </div>

                {/* EXEMPLE CONCRET */}
                <h4>💬 Exemple concret : comment ça marche ?</h4>
                <div className="conversation-example" style={{background: 'rgba(139, 92, 246, 0.1)', borderRadius: '16px', padding: '20px', marginBottom: '24px'}}>
                  <div style={{marginBottom: '16px'}}>
                    <div style={{background: '#1a1a2e', padding: '12px 16px', borderRadius: '12px', marginBottom: '8px', maxWidth: '80%'}}>
                      <strong style={{color: '#d4af37'}}>👤 Toi :</strong>
                      <p style={{margin: '8px 0 0 0', color: '#fff'}}>"Crée-moi un site portfolio moderne avec une page d'accueil, une page projets et une page contact"</p>
                    </div>
                    <div style={{background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', padding: '12px 16px', borderRadius: '12px', marginLeft: '20%'}}>
                      <strong style={{color: '#fff'}}>🤖 Claude Code :</strong>
                      <p style={{margin: '8px 0 0 0', color: '#fff', fontSize: '0.9rem'}}>
                        "Je crée ton portfolio...<br/><br/>
                        ✅ Dossier projet créé<br/>
                        ✅ Page d'accueil avec header et hero section<br/>
                        ✅ Page projets avec galerie<br/>
                        ✅ Page contact avec formulaire<br/>
                        ✅ Navigation responsive<br/><br/>
                        Ton site est prêt ! Lance 'npm start' pour le voir."
                      </p>
                    </div>
                  </div>
                  <p style={{textAlign: 'center', color: 'rgba(255,255,255,0.7)', margin: 0, fontSize: '0.9rem'}}>
                    ⚡ En quelques minutes, tu as un site complet créé sur ton ordinateur !
                  </p>
                </div>

                {/* LE MOMENT AHA */}
                <div className="tip-box" style={{background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(212, 175, 55, 0.05) 100%)', border: '2px solid #d4af37'}}>
                  <div className="tip-icon">🤯</div>
                  <div className="tip-content">
                    <h4>Le moment "Aha!" — Imagine ça :</h4>
                    <p>
                      Tu veux créer une application. Avant, il fallait :<br/>
                      ❌ Apprendre à coder pendant des mois<br/>
                      ❌ Ou payer un développeur 5000€+<br/><br/>
                      Maintenant avec Claude Code :<br/>
                      ✅ Tu décris ce que tu veux<br/>
                      ✅ Il code tout pour toi<br/>
                      ✅ Tu as ton app en quelques heures<br/><br/>
                      <strong>C'est comme avoir un développeur senior gratuit qui travaille 24/7 pour toi ! 🚀</strong>
                    </p>
                  </div>
                </div>

                {/* DIFFÉRENCE CHATGPT VS CLAUDE CODE */}
                <h4>🔀 ChatGPT vs Claude Code — La grande différence</h4>
                <div className="warning-box" style={{background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)', borderColor: '#8b5cf6'}}>
                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px'}}>
                    <div style={{background: 'rgba(16, 185, 129, 0.1)', padding: '16px', borderRadius: '12px'}}>
                      <p style={{fontWeight: 'bold', color: '#10b981', margin: '0 0 12px 0', fontSize: '1.1rem'}}>🧠 ChatGPT</p>
                      <ul style={{margin: 0, paddingLeft: '20px', fontSize: '0.9rem'}}>
                        <li>Te <strong>parle</strong> de code</li>
                        <li>Te <strong>montre</strong> du code</li>
                        <li>Tu dois <strong>copier-coller</strong></li>
                        <li>Ne peut PAS créer de fichiers</li>
                        <li>= Un <strong>conseiller</strong></li>
                      </ul>
                    </div>
                    <div style={{background: 'rgba(139, 92, 246, 0.1)', padding: '16px', borderRadius: '12px'}}>
                      <p style={{fontWeight: 'bold', color: '#8b5cf6', margin: '0 0 12px 0', fontSize: '1.1rem'}}>🛠️ Claude Code</p>
                      <ul style={{margin: 0, paddingLeft: '20px', fontSize: '0.9rem'}}>
                        <li><strong>Crée</strong> les fichiers directement</li>
                        <li><strong>Modifie</strong> ton projet</li>
                        <li><strong>Exécute</strong> des commandes</li>
                        <li>Travaille sur ton ordi</li>
                        <li>= Un <strong>développeur</strong></li>
                      </ul>
                    </div>
                  </div>
                  <p style={{marginTop: '16px', marginBottom: 0, textAlign: 'center', fontSize: '0.95rem', background: 'rgba(212, 175, 55, 0.1)', padding: '12px', borderRadius: '8px'}}>
                    <strong>💡 En résumé : ChatGPT te dit comment faire → Claude Code le FAIT pour toi !</strong>
                  </p>
                </div>

                {/* CE QUE TU PEUX CRÉER */}
                <h4>🎨 Tout ce que tu peux créer avec Claude Code</h4>
                <div className="cards-grid">
                  <div className="info-card" style={{background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.05) 100%)'}}>
                    <div className="card-icon">🌐</div>
                    <h4>Sites web</h4>
                    <p>Portfolios, landing pages, sites vitrines, blogs, e-commerce</p>
                  </div>
                  <div className="info-card" style={{background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.05) 100%)'}}>
                    <div className="card-icon">📱</div>
                    <h4>Applications web</h4>
                    <p>Dashboards, outils de gestion, SaaS, apps métier</p>
                  </div>
                  <div className="info-card" style={{background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.05) 100%)'}}>
                    <div className="card-icon">🤖</div>
                    <h4>Automatisations</h4>
                    <p>Scripts, bots, intégrations API, tâches automatiques</p>
                  </div>
                  <div className="info-card" style={{background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.05) 100%)'}}>
                    <div className="card-icon">📊</div>
                    <h4>Outils sur mesure</h4>
                    <p>Calculateurs, générateurs, analyseurs, convertisseurs</p>
                  </div>
                </div>

                {/* RÉSUMÉ */}
                <div className="tip-box">
                  <div className="tip-icon">🎯</div>
                  <div className="tip-content">
                    <h4>En résumé : Claude Code c'est...</h4>
                    <p>
                      ✅ Un outil qui crée du code sur ton ordinateur<br/>
                      ✅ Tu parles en français, il code pour toi<br/>
                      ✅ Pas besoin de connaissances techniques<br/>
                      ✅ Sites web, apps, automatisations... tout est possible<br/>
                      ✅ Comme avoir un développeur personnel gratuit !
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 2: INSTALLATION ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('installation')}>
              <div className="section-toggle-content">
                <span className="section-number">02</span>
                <div>
                  <h2>Installer Claude Code</h2>
                  <p>Guide pas à pas pour Windows, Mac et Linux</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.installation ? '▼' : '▶'}</span>
            </div>

            {expandedSections.installation && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Installation en 10 minutes</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="installation" />
                </div>

                <div className="warning-box" style={{background: 'rgba(212, 175, 55, 0.1)', borderColor: '#d4af37'}}>
                  <strong>⚠️ Prérequis avant d'installer :</strong>
                  <ul style={{marginBottom: 0}}>
                    <li>Un ordinateur (Windows, Mac ou Linux)</li>
                    <li>Une connexion internet</li>
                    <li>Un compte Anthropic — <strong>gratuit à créer</strong> (on le fait ensemble)</li>
                  </ul>
                </div>

                {/* ÉTAPE 1 : NODE.JS */}
                <h4>📋 Étape 1 : Installer Node.js</h4>
                <div className="info-box">
                  <div className="highlight-box" style={{marginBottom: '16px', background: 'rgba(139, 92, 246, 0.1)'}}>
                    <strong>💡 C'est quoi Node.js ?</strong><br/>
                    Node.js est un logiciel gratuit qui permet de faire fonctionner des outils comme Claude Code.
                    Tu l'installes une seule fois, et après tu n'as plus à t'en occuper.
                  </div>

                  <div style={{background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '12px'}}>
                    <p style={{margin: '0 0 12px 0'}}><strong>🪟 Windows :</strong></p>
                    <ol style={{margin: '0 0 16px 0', paddingLeft: '20px'}}>
                      <li>Ouvre ton navigateur et va sur <strong>nodejs.org</strong></li>
                      <li>Clique sur le gros bouton vert "LTS" (version recommandée)</li>
                      <li>Un fichier se télécharge. Double-clique dessus.</li>
                      <li>Clique "Next" → "Next" → "Next" → "Install" → "Finish"</li>
                    </ol>

                    <p style={{margin: '0 0 12px 0'}}><strong>🍎 Mac :</strong></p>
                    <ol style={{margin: '0 0 16px 0', paddingLeft: '20px'}}>
                      <li>Ouvre ton navigateur et va sur <strong>nodejs.org</strong></li>
                      <li>Clique sur le bouton "LTS" pour macOS</li>
                      <li>Ouvre le fichier .pkg téléchargé</li>
                      <li>Suis les instructions (Continue → Install → mot de passe → Close)</li>
                    </ol>

                    <p style={{margin: '0 0 12px 0'}}><strong>🐧 Linux (Ubuntu/Debian) :</strong></p>
                    <div style={{background: '#1a1a2e', padding: '12px', borderRadius: '6px'}}>
                      <code style={{color: '#10b981', fontSize: '0.9rem'}}>
                        curl -fsSL https://deb.nodesource.com/setup_lts.x | sudo -E bash -<br/>
                        sudo apt-get install -y nodejs
                      </code>
                    </div>
                  </div>
                </div>

                {/* VÉRIFICATION NODE.JS */}
                <div className="tip-box" style={{background: 'rgba(16, 185, 129, 0.1)', border: '1px solid #10b981'}}>
                  <div className="tip-icon">✅</div>
                  <div className="tip-content">
                    <h4>Vérifie que Node.js est bien installé</h4>
                    <p>
                      Ouvre un terminal et tape :<br/>
                      <code style={{background: '#1a1a2e', padding: '4px 8px', borderRadius: '4px', color: '#10b981'}}>node --version</code><br/><br/>
                      <strong>Si tu vois un numéro</strong> (ex: v20.10.0) → C'est bon ! ✅<br/>
                      <strong>Si tu vois une erreur</strong> → Recommence l'étape 1 ou redémarre ton ordinateur.
                    </p>
                  </div>
                </div>

                {/* COMMENT OUVRIR UN TERMINAL */}
                <h4>🖥️ Comment ouvrir un terminal ?</h4>
                <div className="info-box">
                  <p style={{marginBottom: '12px'}}>Le terminal (ou "invite de commandes"), c'est une fenêtre où tu tapes des commandes. Voici comment l'ouvrir :</p>
                  <div style={{display: 'grid', gap: '12px'}}>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '12px 16px', borderRadius: '8px'}}>
                      <strong>🪟 Windows :</strong> Appuie sur la touche Windows, tape <code>cmd</code>, puis Entrée
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '12px 16px', borderRadius: '8px'}}>
                      <strong>🍎 Mac :</strong> Appuie sur Cmd + Espace, tape <code>Terminal</code>, puis Entrée
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '12px 16px', borderRadius: '8px'}}>
                      <strong>🐧 Linux :</strong> Appuie sur Ctrl + Alt + T
                    </div>
                  </div>
                </div>

                {/* ÉTAPE 2 : COMPTE ANTHROPIC */}
                <h4>📋 Étape 2 : Créer un compte Anthropic (gratuit)</h4>
                <div className="info-box">
                  <div className="highlight-box" style={{marginBottom: '16px', background: 'rgba(16, 185, 129, 0.1)'}}>
                    <strong>💰 C'est gratuit ?</strong><br/>
                    Oui ! Créer un compte est 100% gratuit. Tu reçois même <strong>5$ de crédits offerts</strong> pour tester.
                  </div>
                  <div style={{background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '12px'}}>
                    <ol style={{margin: 0, paddingLeft: '20px'}}>
                      <li>Ouvre ton navigateur et va sur <strong>console.anthropic.com</strong></li>
                      <li>Clique sur <strong>"Sign Up"</strong> (bouton en haut à droite)</li>
                      <li>Entre ton adresse email</li>
                      <li>Crée un mot de passe (8 caractères minimum)</li>
                      <li>Va dans ta boîte mail et clique sur le lien de vérification</li>
                      <li>Retourne sur le site et connecte-toi</li>
                    </ol>
                  </div>
                </div>

                {/* ÉTAPE 3 : INSTALLER CLAUDE CODE */}
                <h4>📋 Étape 3 : Installer Claude Code</h4>
                <div className="info-box">
                  <div className="highlight-box" style={{marginBottom: '16px', background: 'rgba(139, 92, 246, 0.1)'}}>
                    <strong>💡 C'est quoi npm ?</strong><br/>
                    npm est un outil qui s'installe automatiquement avec Node.js.
                    Il permet de télécharger et installer des programmes comme Claude Code.
                    Tu n'as pas besoin de comprendre comment ça marche, juste de taper la commande !
                  </div>

                  <p><strong>Dans ton terminal, tape cette commande et appuie sur Entrée :</strong></p>
                  <div style={{background: '#1a1a2e', padding: '16px', borderRadius: '12px', marginTop: '12px'}}>
                    <code style={{color: '#10b981', fontSize: '1.1rem'}}>npm install -g @anthropic-ai/claude-code</code>
                  </div>
                  <p style={{marginTop: '12px', color: 'rgba(255,255,255,0.7)', fontSize: '0.9rem'}}>
                    ⏳ L'installation prend 1-2 minutes. Des lignes de texte vont défiler, c'est normal !
                  </p>
                </div>

                {/* ÉTAPE 4 : SE CONNECTER */}
                <h4>📋 Étape 4 : Se connecter à ton compte</h4>
                <div className="info-box">
                  <p><strong>Dans ton terminal, tape :</strong></p>
                  <div style={{background: '#1a1a2e', padding: '16px', borderRadius: '12px', marginTop: '12px'}}>
                    <code style={{color: '#10b981', fontSize: '1.1rem'}}>claude login</code>
                  </div>
                  <p style={{marginTop: '12px'}}>
                    👉 Une page web va s'ouvrir automatiquement dans ton navigateur.<br/>
                    👉 Connecte-toi avec le compte Anthropic que tu viens de créer.<br/>
                    👉 Clique "Autoriser" quand on te le demande.
                  </p>
                </div>

                {/* SUCCÈS */}
                <div className="tip-box" style={{background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.05) 100%)', border: '2px solid #10b981'}}>
                  <div className="tip-icon">🎉</div>
                  <div className="tip-content">
                    <h4>Bravo ! Claude Code est installé !</h4>
                    <p>
                      Si tu vois un message du genre "Successfully logged in" ou "Bienvenue", c'est bon !<br/><br/>
                      <strong>Pour vérifier que tout marche</strong>, tape : <code style={{background: '#1a1a2e', padding: '4px 8px', borderRadius: '4px', color: '#10b981'}}>claude --version</code><br/>
                      Tu devrais voir un numéro de version (ex: 1.0.0)
                    </p>
                  </div>
                </div>

                {/* PROBLÈMES COURANTS */}
                <h4>🔧 Problèmes courants et solutions</h4>
                <div className="info-box" style={{background: 'rgba(239, 68, 68, 0.05)'}}>
                  <div className="faq-list">
                    <div className="faq-item" style={{borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px', marginBottom: '12px'}}>
                      <div className="faq-q" style={{color: '#ef4444'}}>❌ "npm: command not found" ou "'npm' n'est pas reconnu"</div>
                      <div className="faq-a">
                        Node.js n'est pas correctement installé.<br/>
                        <strong>Solution :</strong> Retourne à l'étape 1, réinstalle Node.js, puis <strong>redémarre ton ordinateur</strong>.
                      </div>
                    </div>
                    <div className="faq-item" style={{borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px', marginBottom: '12px'}}>
                      <div className="faq-q" style={{color: '#ef4444'}}>❌ "claude: command not found"</div>
                      <div className="faq-a">
                        L'installation n'est pas terminée ou le terminal n'est pas à jour.<br/>
                        <strong>Solution :</strong> Ferme ton terminal, réouvre-le, et réessaie. Si ça ne marche toujours pas, réinstalle avec la commande npm.
                      </div>
                    </div>
                    <div className="faq-item" style={{borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '12px', marginBottom: '12px'}}>
                      <div className="faq-q" style={{color: '#ef4444'}}>❌ "EACCES: permission denied" (Mac/Linux)</div>
                      <div className="faq-a">
                        Tu n'as pas les droits d'installation.<br/>
                        <strong>Solution :</strong> Ajoute <code>sudo</code> devant la commande :<br/>
                        <code style={{background: '#1a1a2e', padding: '4px 8px', borderRadius: '4px', color: '#10b981'}}>sudo npm install -g @anthropic-ai/claude-code</code><br/>
                        (Tape ton mot de passe quand demandé)
                      </div>
                    </div>
                    <div className="faq-item">
                      <div className="faq-q" style={{color: '#ef4444'}}>❌ La page de connexion ne s'ouvre pas</div>
                      <div className="faq-a">
                        <strong>Solution :</strong> Copie l'URL qui s'affiche dans le terminal et colle-la manuellement dans ton navigateur.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 3: PREMIER LANCEMENT ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('launch')}>
              <div className="section-toggle-content">
                <span className="section-number">03</span>
                <div>
                  <h2>Lancer et utiliser Claude Code</h2>
                  <p>Comment démarrer et parler à Claude Code</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.launch ? '▼' : '▶'}</span>
            </div>

            {expandedSections.launch && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Ton premier lancement</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="launch" />
                </div>

                <h4>🚀 Comment lancer Claude Code en 3 étapes</h4>

                {/* ÉTAPE 1 : OUVRIR TERMINAL */}
                <div className="info-box">
                  <p><strong>Étape 1 : Ouvre un terminal</strong></p>
                  <div style={{display: 'grid', gap: '8px', marginTop: '12px'}}>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '10px 14px', borderRadius: '8px'}}>
                      <strong>🪟 Windows :</strong> Touche Windows → tape <code>cmd</code> → Entrée
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '10px 14px', borderRadius: '8px'}}>
                      <strong>🍎 Mac :</strong> Cmd + Espace → tape <code>Terminal</code> → Entrée
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '10px 14px', borderRadius: '8px'}}>
                      <strong>🐧 Linux :</strong> Ctrl + Alt + T
                    </div>
                  </div>
                </div>

                {/* ÉTAPE 2 : ALLER DANS UN DOSSIER */}
                <div className="info-box">
                  <p><strong>Étape 2 : Va dans le dossier où tu veux créer ton projet</strong></p>

                  <div className="highlight-box" style={{marginTop: '12px', marginBottom: '16px', background: 'rgba(139, 92, 246, 0.1)'}}>
                    <strong>💡 C'est quoi la commande "cd" ?</strong><br/>
                    <code>cd</code> = "change directory" = aller dans un dossier.<br/>
                    C'est comme double-cliquer sur un dossier, mais en version terminal !
                  </div>

                  <p style={{marginBottom: '12px'}}><strong>Exemples de commandes selon ton système :</strong></p>

                  <div style={{background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '12px', marginBottom: '12px'}}>
                    <p style={{margin: '0 0 8px 0'}}><strong>🪟 Windows</strong> — Pour aller sur le Bureau :</p>
                    <code style={{background: '#1a1a2e', padding: '8px 12px', borderRadius: '6px', display: 'block', color: '#10b981'}}>
                      cd %USERPROFILE%\Desktop
                    </code>
                    <p style={{margin: '12px 0 8px 0'}}>Ou pour créer un dossier "projets" et y aller :</p>
                    <code style={{background: '#1a1a2e', padding: '8px 12px', borderRadius: '6px', display: 'block', color: '#10b981'}}>
                      mkdir %USERPROFILE%\projets && cd %USERPROFILE%\projets
                    </code>
                  </div>

                  <div style={{background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '12px', marginBottom: '12px'}}>
                    <p style={{margin: '0 0 8px 0'}}><strong>🍎 Mac</strong> — Pour aller sur le Bureau :</p>
                    <code style={{background: '#1a1a2e', padding: '8px 12px', borderRadius: '6px', display: 'block', color: '#10b981'}}>
                      cd ~/Desktop
                    </code>
                    <p style={{margin: '12px 0 8px 0'}}>Ou pour créer un dossier "projets" et y aller :</p>
                    <code style={{background: '#1a1a2e', padding: '8px 12px', borderRadius: '6px', display: 'block', color: '#10b981'}}>
                      mkdir -p ~/projets && cd ~/projets
                    </code>
                  </div>

                  <div style={{background: 'rgba(255,255,255,0.05)', padding: '16px', borderRadius: '12px'}}>
                    <p style={{margin: '0 0 8px 0'}}><strong>🐧 Linux</strong> — Pour créer un dossier "projets" et y aller :</p>
                    <code style={{background: '#1a1a2e', padding: '8px 12px', borderRadius: '6px', display: 'block', color: '#10b981'}}>
                      mkdir -p ~/projets && cd ~/projets
                    </code>
                  </div>
                </div>

                {/* ÉTAPE 3 : LANCER CLAUDE */}
                <div className="info-box">
                  <p><strong>Étape 3 : Lance Claude Code !</strong></p>
                  <div style={{background: '#1a1a2e', padding: '16px', borderRadius: '12px', marginTop: '12px'}}>
                    <code style={{color: '#10b981', fontSize: '1.2rem'}}>claude</code>
                  </div>
                  <p style={{marginTop: '12px', color: 'rgba(255,255,255,0.7)'}}>
                    🎉 C'est tout ! Claude Code se lance et tu peux commencer à lui parler.
                  </p>
                </div>

                <h4>💬 L'interface de Claude Code</h4>
                <div style={{background: 'rgba(139, 92, 246, 0.1)', borderRadius: '16px', padding: '20px', marginBottom: '24px'}}>
                  <div style={{background: '#0a0a0f', padding: '20px', borderRadius: '12px', fontFamily: 'monospace'}}>
                    <p style={{color: '#8b5cf6', margin: '0 0 8px 0'}}>╭─────────────────────────────────────╮</p>
                    <p style={{color: '#8b5cf6', margin: '0 0 8px 0'}}>│  Claude Code v1.0                   │</p>
                    <p style={{color: '#8b5cf6', margin: '0 0 16px 0'}}>╰─────────────────────────────────────╯</p>
                    <p style={{color: '#10b981', margin: '0 0 8px 0'}}>Bienvenue ! Je suis Claude Code.</p>
                    <p style={{color: '#10b981', margin: '0 0 16px 0'}}>Je peux créer et modifier des fichiers sur ton ordinateur.</p>
                    <p style={{color: '#fff', margin: 0}}>👤 <span style={{color: '#d4af37'}}>Toi ▶</span> _</p>
                  </div>
                  <p style={{textAlign: 'center', color: 'rgba(255,255,255,0.7)', margin: '16px 0 0 0', fontSize: '0.9rem'}}>
                    Tu peux maintenant écrire ce que tu veux créer !
                  </p>
                </div>

                <h4>📝 Tes premières commandes</h4>
                <div className="info-box">
                  <p><strong>Exemples de choses que tu peux demander :</strong></p>
                  <div style={{display: 'grid', gap: '12px', marginTop: '12px'}}>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '12px 16px', borderRadius: '8px'}}>
                      <code style={{color: '#10b981'}}>"Crée un site portfolio simple"</code>
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '12px 16px', borderRadius: '8px'}}>
                      <code style={{color: '#10b981'}}>"Ajoute une page contact avec un formulaire"</code>
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '12px 16px', borderRadius: '8px'}}>
                      <code style={{color: '#10b981'}}>"Change la couleur du header en bleu"</code>
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '12px 16px', borderRadius: '8px'}}>
                      <code style={{color: '#10b981'}}>"Explique-moi ce que fait ce fichier"</code>
                    </div>
                  </div>
                </div>

                <h4>⌨️ Les commandes utiles</h4>
                <div className="comparison-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Commande</th>
                        <th>Ce que ça fait</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td><code>/help</code></td>
                        <td>Affiche l'aide</td>
                      </tr>
                      <tr>
                        <td><code>/clear</code></td>
                        <td>Efface la conversation</td>
                      </tr>
                      <tr>
                        <td><code>/compact</code></td>
                        <td>Résume la conversation (économise des tokens)</td>
                      </tr>
                      <tr>
                        <td><code>Ctrl + C</code></td>
                        <td>Arrêter Claude Code</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="tip-box">
                  <div className="tip-icon">💡</div>
                  <div className="tip-content">
                    <h4>Conseil : Parle naturellement !</h4>
                    <p>
                      Tu n'as pas besoin d'utiliser des termes techniques.<br/>
                      Dis simplement ce que tu veux, comme si tu parlais à un ami développeur.<br/><br/>
                      ❌ "Implémente un composant React avec useState"<br/>
                      ✅ "Ajoute un bouton qui compte les clics"
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 4: LES SUPER-POUVOIRS ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('powers')}>
              <div className="section-toggle-content">
                <span className="section-number">04</span>
                <div>
                  <h2>Les Super-Pouvoirs de Claude Code</h2>
                  <p>Tout ce que Claude Code peut faire pour toi</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.powers ? '▼' : '▶'}</span>
            </div>

            {expandedSections.powers && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Découvre ses capacités</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="powers" />
                </div>

                <div className="highlight-box gold">
                  <strong>🦸 Claude Code peut :</strong><br/><br/>
                  Créer, modifier, supprimer des fichiers • Lire ton projet entier • Exécuter des commandes •
                  Installer des packages • Corriger des bugs • Expliquer du code • Et bien plus !
                </div>

                <h4>💪 Ses super-pouvoirs en détail</h4>
                <div style={{display: 'grid', gap: '16px'}}>
                  <div className="info-box" style={{background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, transparent 100%)'}}>
                    <h5 style={{color: '#10b981', marginTop: 0}}>📁 Gestion des fichiers</h5>
                    <ul style={{marginBottom: 0}}>
                      <li><strong>Créer</strong> des fichiers et dossiers</li>
                      <li><strong>Modifier</strong> le contenu existant</li>
                      <li><strong>Supprimer</strong> ce qui n'est plus utile</li>
                      <li><strong>Organiser</strong> la structure du projet</li>
                    </ul>
                  </div>

                  <div className="info-box" style={{background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, transparent 100%)'}}>
                    <h5 style={{color: '#8b5cf6', marginTop: 0}}>👁️ Vision globale</h5>
                    <ul style={{marginBottom: 0}}>
                      <li><strong>Lit</strong> tous les fichiers de ton projet</li>
                      <li><strong>Comprend</strong> comment tout est connecté</li>
                      <li><strong>Détecte</strong> les problèmes et incohérences</li>
                      <li><strong>Suggère</strong> des améliorations</li>
                    </ul>
                  </div>

                  <div className="info-box" style={{background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, transparent 100%)'}}>
                    <h5 style={{color: '#d4af37', marginTop: 0}}>⚡ Exécution de commandes</h5>
                    <ul style={{marginBottom: 0}}>
                      <li><strong>npm install</strong> — Installer des packages</li>
                      <li><strong>npm start</strong> — Lancer ton app</li>
                      <li><strong>git</strong> — Gérer les versions</li>
                      <li>Et toutes les commandes terminal !</li>
                    </ul>
                  </div>

                  <div className="info-box" style={{background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, transparent 100%)'}}>
                    <h5 style={{color: '#ef4444', marginTop: 0}}>🐛 Debugging</h5>
                    <ul style={{marginBottom: 0}}>
                      <li><strong>Trouve</strong> les bugs dans ton code</li>
                      <li><strong>Explique</strong> pourquoi ça ne marche pas</li>
                      <li><strong>Corrige</strong> automatiquement</li>
                      <li><strong>Teste</strong> que ça fonctionne</li>
                    </ul>
                  </div>
                </div>

                <h4>🎯 Exemples concrets de demandes</h4>
                <div className="info-box">
                  <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px'}}>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '12px', borderRadius: '8px'}}>
                      <p style={{margin: 0, fontSize: '0.9rem'}}>"Crée une landing page pour mon produit"</p>
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '12px', borderRadius: '8px'}}>
                      <p style={{margin: 0, fontSize: '0.9rem'}}>"Ajoute un mode sombre à mon site"</p>
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '12px', borderRadius: '8px'}}>
                      <p style={{margin: 0, fontSize: '0.9rem'}}>"Ce bouton ne marche pas, répare-le"</p>
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '12px', borderRadius: '8px'}}>
                      <p style={{margin: 0, fontSize: '0.9rem'}}>"Rends mon site responsive mobile"</p>
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '12px', borderRadius: '8px'}}>
                      <p style={{margin: 0, fontSize: '0.9rem'}}>"Ajoute une connexion utilisateur"</p>
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '12px', borderRadius: '8px'}}>
                      <p style={{margin: 0, fontSize: '0.9rem'}}>"Connecte mon app à une base de données"</p>
                    </div>
                  </div>
                </div>

                <div className="tip-box">
                  <div className="tip-icon">🚀</div>
                  <div className="tip-content">
                    <h4>Le plus impressionnant ?</h4>
                    <p>
                      Claude Code comprend le <strong>contexte de ton projet</strong>.<br/>
                      Si tu lui dis "ajoute un bouton de connexion", il sait déjà :<br/>
                      • Quelle technologie tu utilises (React, Vue, etc.)<br/>
                      • Le style de ton site (couleurs, fonts, etc.)<br/>
                      • Comment ton code est organisé<br/><br/>
                      Il s'adapte automatiquement ! 🎯
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 5: TARIFS ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('pricing')}>
              <div className="section-toggle-content">
                <span className="section-number">05</span>
                <div>
                  <h2>Combien ça coûte ?</h2>
                  <p>Les tarifs de Claude Code expliqués simplement</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.pricing ? '▼' : '▶'}</span>
            </div>

            {expandedSections.pricing && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Les tarifs Anthropic</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="pricing" />
                </div>

                <div className="version-cards">
                  <div className="version-card free" style={{borderColor: '#10b981'}}>
                    <div className="version-header">
                      <h4>Essai gratuit</h4>
                      <span className="version-badge free">GRATUIT</span>
                    </div>
                    <div className="version-content">
                      <p className="version-desc">Pour découvrir et tester</p>
                      <ul>
                        <li>✅ 5$ de crédits offerts</li>
                        <li>✅ Accès à Claude Code</li>
                        <li>✅ Toutes les fonctionnalités</li>
                        <li>⚠️ Environ 1-2 petits projets</li>
                      </ul>
                      <div className="version-use">
                        <strong>Idéal pour :</strong> Tester et apprendre
                      </div>
                    </div>
                  </div>

                  <div className="version-card best" style={{borderColor: '#8b5cf6'}}>
                    <div className="version-header">
                      <h4>Claude Pro</h4>
                      <span className="version-badge best">20$/mois (~18€)</span>
                    </div>
                    <div className="version-content">
                      <p className="version-desc">Pour un usage régulier</p>
                      <ul>
                        <li>✅ Usage quasi-illimité</li>
                        <li>✅ Priorité sur les serveurs</li>
                        <li>✅ Accès aux derniers modèles</li>
                        <li>✅ Plusieurs projets par jour</li>
                      </ul>
                      <div className="version-use">
                        <strong>Idéal pour :</strong> Créateurs et freelances
                      </div>
                    </div>
                  </div>

                  <div className="version-card enterprise" style={{borderColor: '#d4af37'}}>
                    <div className="version-header">
                      <h4>API (Pay as you go)</h4>
                      <span className="version-badge enterprise">À L'USAGE</span>
                    </div>
                    <div className="version-content">
                      <p className="version-desc">Payer selon ta consommation</p>
                      <ul>
                        <li>✅ Pas d'abonnement mensuel</li>
                        <li>✅ Tu paies uniquement ce que tu utilises</li>
                        <li>✅ Recharge ton compte quand tu veux</li>
                        <li>✅ Contrôle total des dépenses</li>
                      </ul>
                      <div className="version-use">
                        <strong>Idéal pour :</strong> Usage occasionnel
                      </div>
                    </div>
                  </div>
                </div>

                {/* EXEMPLES CONCRETS DE COÛTS */}
                <h4>💰 Exemples concrets : combien ça coûte vraiment ?</h4>
                <div className="info-box">
                  <p style={{marginBottom: '16px'}}>Voici des exemples réels de ce que coûte un projet avec l'API (paiement à l'usage) :</p>
                  <div style={{display: 'grid', gap: '12px'}}>
                    <div style={{background: 'rgba(16, 185, 129, 0.1)', padding: '16px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <div>
                        <strong>🌐 Site portfolio simple</strong>
                        <p style={{margin: '4px 0 0 0', fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)'}}>5-10 pages, design moderne</p>
                      </div>
                      <span style={{background: '#10b981', padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', fontSize: '1.1rem'}}>~2-5$</span>
                    </div>
                    <div style={{background: 'rgba(139, 92, 246, 0.1)', padding: '16px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <div>
                        <strong>📱 Application web complète</strong>
                        <p style={{margin: '4px 0 0 0', fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)'}}>Dashboard, formulaires, base de données</p>
                      </div>
                      <span style={{background: '#8b5cf6', padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', fontSize: '1.1rem'}}>~10-20$</span>
                    </div>
                    <div style={{background: 'rgba(212, 175, 55, 0.1)', padding: '16px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                      <div>
                        <strong>🚀 Gros projet SaaS</strong>
                        <p style={{margin: '4px 0 0 0', fontSize: '0.9rem', color: 'rgba(255,255,255,0.7)'}}>Authentification, paiements, admin, etc.</p>
                      </div>
                      <span style={{background: '#d4af37', padding: '8px 16px', borderRadius: '8px', fontWeight: 'bold', fontSize: '1.1rem', color: '#000'}}>~30-50$</span>
                    </div>
                  </div>
                </div>

                {/* RECOMMANDATION */}
                <div className="tip-box" style={{background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.05) 100%)', border: '2px solid #10b981'}}>
                  <div className="tip-icon">🎯</div>
                  <div className="tip-content">
                    <h4>Notre recommandation F12IA™</h4>
                    <p>
                      <strong>🆓 Pour commencer :</strong> Utilise les 5$ de crédits gratuits pour tester et faire ton premier projet.<br/><br/>
                      <strong>💼 Pour créer régulièrement :</strong> Claude Pro à 20$/mois (~18€) est le meilleur choix. Tu peux créer plusieurs projets par jour sans compter.<br/><br/>
                      <strong>💡 Calcul rentabilité :</strong><br/>
                      • Tu paies 20$/mois = 240$/an (~220€)<br/>
                      • Tu vends UN seul site à 500€<br/>
                      • 👉 Tu as remboursé <strong>2 ans d'abonnement</strong> !
                    </p>
                  </div>
                </div>

                {/* COMMENT PAYER */}
                <div className="info-box" style={{background: 'rgba(212, 175, 55, 0.05)'}}>
                  <h5 style={{marginTop: 0, color: '#d4af37'}}>💳 Comment payer ?</h5>
                  <p>
                    Tu peux payer par <strong>carte bancaire</strong> (Visa, Mastercard, etc.).<br/>
                    Pour l'abonnement Pro, le paiement est mensuel et tu peux annuler quand tu veux.<br/>
                    Pour l'API, tu recharges ton compte avec le montant que tu veux (minimum 5$).
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 6: PREMIER PROJET ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('firstproject')}>
              <div className="section-toggle-content">
                <span className="section-number">06</span>
                <div>
                  <h2>Crée ton premier projet</h2>
                  <p>Exercice guidé : créer un portfolio en 15 minutes</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.firstproject ? '▼' : '▶'}</span>
            </div>

            {expandedSections.firstproject && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>🎯 Objectif : Créer un site portfolio</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="firstproject" />
                </div>

                <div className="highlight-box gold">
                  <strong>Ce que tu vas créer :</strong><br/>
                  Un site portfolio professionnel avec :<br/>
                  • Page d'accueil avec ta présentation<br/>
                  • Section projets/réalisations<br/>
                  • Page contact avec formulaire<br/>
                  • Design moderne et responsive
                </div>

                <h4>📋 Étape 1 : Prépare ton espace</h4>
                <div className="info-box">
                  <p>Ouvre un terminal et crée un dossier pour ton projet :</p>
                  <div style={{background: '#1a1a2e', padding: '16px', borderRadius: '12px', marginTop: '12px'}}>
                    <code style={{color: '#10b981', display: 'block', marginBottom: '8px'}}>mkdir mon-portfolio</code>
                    <code style={{color: '#10b981', display: 'block', marginBottom: '8px'}}>cd mon-portfolio</code>
                    <code style={{color: '#10b981', display: 'block'}}>claude</code>
                  </div>
                </div>

                <h4>📋 Étape 2 : Donne le brief à Claude Code</h4>
                <div className="info-box">
                  <p>Copie ce texte et colle-le dans Claude Code :</p>
                </div>
                <PromptBox prompt={`Crée-moi un site portfolio professionnel en React.

MON PROFIL :
- Nom : [Ton nom]
- Métier : [Ton métier ou "Créateur digital"]
- Description : [Une phrase sur toi]

PAGES À CRÉER :
1. Accueil - Hero section avec mon nom et une accroche
2. À propos - Ma présentation
3. Projets - Grille de 4-6 projets (avec images placeholder)
4. Contact - Formulaire (nom, email, message)

DESIGN :
- Style moderne et minimaliste
- Couleur principale : bleu (#3B82F6)
- Mode sombre
- Responsive (mobile-friendly)
- Animations subtiles au scroll

Crée toute la structure et le code. Utilise Create React App.`} title="Brief pour ton portfolio" />

                <h4>📋 Étape 3 : Laisse Claude Code travailler</h4>
                <div className="info-box">
                  <p>Claude Code va :</p>
                  <ol>
                    <li>Créer le projet React</li>
                    <li>Installer les dépendances</li>
                    <li>Créer toutes les pages</li>
                    <li>Ajouter le style CSS</li>
                    <li>Configurer la navigation</li>
                  </ol>
                  <p style={{marginTop: '12px', color: 'rgba(255,255,255,0.7)'}}>
                    💡 <strong>Accepte les actions</strong> quand Claude Code te demande la permission de créer/modifier des fichiers.
                  </p>
                </div>

                <h4>📋 Étape 4 : Lance ton site</h4>
                <div className="info-box">
                  <p>Une fois que Claude Code a fini, tape :</p>
                  <div style={{background: '#1a1a2e', padding: '16px', borderRadius: '12px', marginTop: '12px'}}>
                    <code style={{color: '#10b981'}}>npm start</code>
                  </div>
                  <p style={{marginTop: '12px'}}>
                    🎉 Ton navigateur s'ouvre avec ton portfolio !<br/>
                    Il est accessible sur <strong>http://localhost:3000</strong>
                  </p>
                </div>

                <h4>📋 Étape 5 : Personnalise !</h4>
                <div className="info-box">
                  <p>Demande à Claude Code de modifier ce que tu veux :</p>
                  <div style={{display: 'grid', gap: '8px', marginTop: '12px'}}>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '10px 14px', borderRadius: '8px'}}>
                      <code style={{color: '#10b981'}}>"Change la couleur en violet"</code>
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '10px 14px', borderRadius: '8px'}}>
                      <code style={{color: '#10b981'}}>"Ajoute mes vrais projets : [liste]"</code>
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '10px 14px', borderRadius: '8px'}}>
                      <code style={{color: '#10b981'}}>"Mets ma vraie photo en hero"</code>
                    </div>
                    <div style={{background: 'rgba(255,255,255,0.05)', padding: '10px 14px', borderRadius: '8px'}}>
                      <code style={{color: '#10b981'}}>"Ajoute un lien vers mon LinkedIn"</code>
                    </div>
                  </div>
                </div>

                <div className="tip-box" style={{background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.05) 100%)', border: '2px solid #10b981'}}>
                  <div className="tip-icon">🎉</div>
                  <div className="tip-content">
                    <h4>Bravo ! Tu as créé ton premier site !</h4>
                    <p>
                      En quelques minutes, tu as un portfolio professionnel.<br/>
                      Sans écrire une seule ligne de code toi-même !<br/><br/>
                      <strong>Prochaine étape :</strong> Le mettre en ligne (Section Déploiement)
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 7: EXERCICE PLATEFORME ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('exercise')}>
              <div className="section-toggle-content">
                <span className="section-number">07</span>
                <div>
                  <h2>Exercice avancé : Créer une plateforme</h2>
                  <p>Crée une vraie application complète pas à pas</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.exercise ? '▼' : '▶'}</span>
            </div>

            {expandedSections.exercise && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>🚀 Projet : Plateforme de gestion de tâches</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="exercise" />
                </div>

                <div className="highlight-box gold">
                  <strong>Ce que tu vas créer :</strong><br/>
                  Une application complète de gestion de tâches (comme Trello simplifié) avec :<br/>
                  • Création de tâches<br/>
                  • Organisation par colonnes (À faire, En cours, Terminé)<br/>
                  • Drag & drop<br/>
                  • Sauvegarde locale<br/>
                  • Design professionnel
                </div>

                <h4>📋 Phase 1 : Le brief initial</h4>
                <PromptBox prompt={`Crée une application de gestion de tâches (style Trello) en React.

FONCTIONNALITÉS :
1. 3 colonnes : "À faire", "En cours", "Terminé"
2. Ajouter une nouvelle tâche (titre + description)
3. Glisser-déposer les tâches entre colonnes
4. Supprimer une tâche
5. Sauvegarde automatique dans localStorage

DESIGN :
- Style moderne, fond sombre
- Cards avec ombre légère
- Couleur principale : violet (#8B5CF6)
- Responsive
- Animations fluides

STRUCTURE :
- Composant Board (contient les colonnes)
- Composant Column (une colonne)
- Composant Task (une tâche)
- Composant AddTask (formulaire)

Commence par créer le projet et la structure de base.`} title="Brief - Phase 1 : Structure" />

                <h4>📋 Phase 2 : Ajouter des fonctionnalités</h4>
                <div className="info-box">
                  <p>Une fois la base créée, demande ces améliorations une par une :</p>
                </div>

                <PromptBox prompt={`Ajoute ces fonctionnalités :

1. Labels colorés sur les tâches (urgent, normal, faible priorité)
2. Date d'échéance avec indicateur visuel (rouge si dépassé)
3. Filtrer les tâches par label
4. Compteur de tâches par colonne`} title="Brief - Phase 2 : Fonctionnalités" />

                <h4>📋 Phase 3 : Améliorer l'UX</h4>
                <PromptBox prompt={`Améliore l'expérience utilisateur :

1. Modal pour éditer une tâche (au clic)
2. Confirmation avant suppression
3. Animation quand une tâche change de colonne
4. Raccourci clavier : Entrée pour ajouter vite une tâche
5. Message de bienvenue si aucune tâche`} title="Brief - Phase 3 : UX" />

                <h4>📋 Phase 4 : Finalisation</h4>
                <PromptBox prompt={`Pour finir :

1. Ajoute un header avec le titre "TaskFlow" et un bouton "Tout effacer"
2. Footer avec "Créé avec Claude Code"
3. Favicon et titre de page
4. Vérifie que tout est responsive
5. Corrige les éventuels bugs`} title="Brief - Phase 4 : Finalisation" />

                <div className="tip-box" style={{background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(212, 175, 55, 0.05) 100%)', border: '2px solid #d4af37'}}>
                  <div className="tip-icon">🏆</div>
                  <div className="tip-content">
                    <h4>Tu viens de créer une vraie application !</h4>
                    <p>
                      Cette app pourrait être vendue ou utilisée professionnellement.<br/>
                      Tu as appris à :<br/>
                      ✅ Donner des briefs clairs à Claude Code<br/>
                      ✅ Construire par phases (itérations)<br/>
                      ✅ Demander des améliorations progressives<br/>
                      ✅ Créer une app complète et fonctionnelle<br/><br/>
                      <strong>Tu es maintenant capable de créer n'importe quelle application ! 🚀</strong>
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 8: RÉDIGER DES BRIEFS ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('briefs')}>
              <div className="section-toggle-content">
                <span className="section-number">08</span>
                <div>
                  <h2>L'Art du Brief Parfait</h2>
                  <p>Comment communiquer avec Claude Code pour des résultats optimaux</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.briefs ? '▼' : '▶'}</span>
            </div>

            {expandedSections.briefs && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Structure d'un bon brief</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="briefs" />
                </div>

                <p>La qualité de votre brief détermine la qualité du résultat. Voici la structure optimale :</p>

                <div className="track-steps">
                  <div className="track-step">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <strong>Contexte & Objectif</strong>
                      <p>Expliquez le "pourquoi" : quel problème résolvez-vous ? Pour qui ?</p>
                    </div>
                  </div>
                  <div className="track-step">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <strong>Fonctionnalités</strong>
                      <p>Listez ce que l'app doit faire. Soyez précis et priorisez.</p>
                    </div>
                  </div>
                  <div className="track-step">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <strong>Parcours utilisateur</strong>
                      <p>Décrivez le flux : l'utilisateur arrive, il fait X, il obtient Y.</p>
                    </div>
                  </div>
                  <div className="track-step">
                    <div className="step-number">4</div>
                    <div className="step-content">
                      <strong>Design & Contraintes</strong>
                      <p>Style visuel, couleurs, technologies imposées...</p>
                    </div>
                  </div>
                </div>

                <h3>Template de brief</h3>
                <PromptBox prompt={`Crée une application web [NOM].

## Contexte
[Qui êtes-vous ? Quel problème résolvez-vous ? Pour qui ?]

## Fonctionnalités MVP
1. [Feature principale - la plus importante]
2. [Feature secondaire]
3. [Feature secondaire]

## Parcours utilisateur
1. L'utilisateur arrive sur la page d'accueil
2. Il voit [quoi] et clique sur [quoi]
3. Il remplit [formulaire/info]
4. Il obtient [résultat/valeur]

## Design
- Style : [moderne/minimaliste/corporate/fun]
- Couleur principale : [couleur ou hex]
- Inspiration : [sites/apps similaires]

## Contraintes techniques
- Doit fonctionner sur mobile
- [Autres contraintes]

Commence par créer la structure du projet.`} title="Template Brief Claude Code" />

                <h3>Générateur de brief</h3>
                <PromptPlayground
                  variables={projectVariables}
                  template={projectTemplate}
                  title="Créez votre brief personnalisé"
                />

                <div className="tip-box">
                  <div className="tip-icon">💡</div>
                  <div className="tip-content">
                    <h4>Conseil pro</h4>
                    <p>Commencez petit, puis itérez. Demandez d'abord la page principale,
                       testez-la, puis demandez les fonctionnalités suivantes une par une.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 9: CRÉER UN MVP ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('mvp')}>
              <div className="section-toggle-content">
                <span className="section-number">09</span>
                <div>
                  <h2>Créer votre MVP</h2>
                  <p>Guide pas à pas pour créer votre première application</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.mvp ? '▼' : '▶'}</span>
            </div>

            {expandedSections.mvp && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>La méthode MVP</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="mvp" />
                </div>

                <div className="highlight-box">
                  <strong>MVP = Minimum Viable Product</strong><br/>
                  La version la plus simple de votre produit qui apporte de la valeur.
                  Pas besoin d'être parfait — juste fonctionnel.
                </div>

                <div className="two-columns">
                  <div>
                    <h4 style={{color: '#e74c3c'}}>❌ Mauvaise approche</h4>
                    <ul className="module-list cross">
                      <li>Passer 6 mois à développer</li>
                      <li>Ajouter toutes les features</li>
                      <li>Attendre la perfection</li>
                      <li>Ne jamais montrer à personne</li>
                    </ul>
                  </div>
                  <div>
                    <h4 style={{color: '#27ae60'}}>✅ Bonne approche</h4>
                    <ul className="module-list check">
                      <li>Lancer en 1-2 semaines</li>
                      <li>3-5 features max</li>
                      <li>Tester avec de vrais utilisateurs</li>
                      <li>Itérer selon les retours</li>
                    </ul>
                  </div>
                </div>

                <h3>Guide : De l'idée à l'app</h3>
                <StepByStepGuide
                  moduleId={MODULE_ID}
                  guideId="create-mvp"
                  title="Créer votre MVP en 5 étapes"
                  steps={appGuideSteps}
                />

                <h3>Exemples de projets réalisables en 1 jour</h3>
                <div className="cards-grid">
                  <div className="info-card">
                    <div className="card-icon">📅</div>
                    <h4>Système de réservation</h4>
                    <p>Calendrier, créneaux, confirmations</p>
                  </div>
                  <div className="info-card">
                    <div className="card-icon">📝</div>
                    <h4>Générateur de contenu</h4>
                    <p>Input → IA → Output personnalisé</p>
                  </div>
                  <div className="info-card">
                    <div className="card-icon">💰</div>
                    <h4>Calculateur/Simulateur</h4>
                    <p>Devis auto, ROI, comparateurs</p>
                  </div>
                  <div className="info-card">
                    <div className="card-icon">📊</div>
                    <h4>Dashboard</h4>
                    <p>KPIs, graphiques, analytics</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 10: DÉPLOIEMENT ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('deploy')}>
              <div className="section-toggle-content">
                <span className="section-number">10</span>
                <div>
                  <h2>Déployer en Production</h2>
                  <p>Mettre votre app en ligne pour le monde entier</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.deploy ? '▼' : '▶'}</span>
            </div>

            {expandedSections.deploy && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>La stack gratuite recommandée</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="deploy" />
                </div>

                <div className="cards-grid">
                  <div className="info-card">
                    <div className="card-icon">🚀</div>
                    <h4>Vercel</h4>
                    <p>Hébergement frontend, déploiement auto depuis GitHub</p>
                  </div>
                  <div className="info-card">
                    <div className="card-icon">🗄️</div>
                    <h4>Supabase</h4>
                    <p>Base de données + Auth + API (gratuit généreux)</p>
                  </div>
                  <div className="info-card">
                    <div className="card-icon">💳</div>
                    <h4>Stripe</h4>
                    <p>Paiements (0€ jusqu'à vos premières ventes)</p>
                  </div>
                  <div className="info-card">
                    <div className="card-icon">🌐</div>
                    <h4>Namecheap</h4>
                    <p>Domaine personnalisé (~10€/an)</p>
                  </div>
                </div>

                <h3>Déployer sur Vercel</h3>
                <PromptBox prompt={`Mon app est prête localement. Guide-moi pour :

1. Pousser mon code sur GitHub
2. Connecter le repo à Vercel
3. Configurer les variables d'environnement
4. Déployer et obtenir mon URL
5. Connecter mon domaine personnalisé [domaine.com]

Donne-moi les commandes exactes et les étapes dans Vercel.`} title="Prompt déploiement" />

                <h3>Checklist avant mise en ligne</h3>
                <div className="summary-box">
                  <ul>
                    <li><strong>✅ MVP fonctionnel</strong> — Toutes les features de base marchent</li>
                    <li><strong>✅ Mobile-friendly</strong> — Testé sur téléphone</li>
                    <li><strong>✅ Page d'accueil claire</strong> — On comprend la valeur en 5 sec</li>
                    <li><strong>✅ Pas de bugs critiques</strong> — Le parcours principal fonctionne</li>
                    <li><strong>✅ Contact visible</strong> — Les utilisateurs peuvent vous joindre</li>
                  </ul>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 11: MONÉTISATION ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('business')}>
              <div className="section-toggle-content">
                <span className="section-number">11</span>
                <div>
                  <h2>Monétiser vos Créations</h2>
                  <p>Transformer vos compétences en revenus</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.business ? '▼' : '▶'}</span>
            </div>

            {expandedSections.business && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Opportunités de revenus</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="business" />
                </div>

                <div className="business-cards">
                  <div className="business-card">
                    <div className="price">500-2000€</div>
                    <h4>Site web professionnel</h4>
                    <p>Landing pages, sites vitrines, portfolios pour clients</p>
                  </div>
                  <div className="business-card">
                    <div className="price">1000-5000€</div>
                    <h4>Application web</h4>
                    <p>Outils métier, dashboards, apps sur mesure</p>
                  </div>
                  <div className="business-card">
                    <div className="price">50-500€/mois</div>
                    <h4>SaaS (revenus récurrents)</h4>
                    <p>Votre propre produit avec abonnement</p>
                  </div>
                  <div className="business-card">
                    <div className="price">500-1500€/jour</div>
                    <h4>Consulting IA</h4>
                    <p>Aider les entreprises à adopter l'IA</p>
                  </div>
                </div>

                <h3>Intégrer les paiements avec Stripe</h3>
                <PromptBox prompt={`Intègre Stripe dans mon application [NOM].

MODÈLE : Abonnement mensuel

PLANS :
- Basic : 19€/mois - [features]
- Pro : 49€/mois - [features]
- Enterprise : 99€/mois - [features]

FONCTIONNALITÉS :
1. Page de pricing avec les 3 plans
2. Checkout Stripe sécurisé
3. Webhooks pour activer les features après paiement
4. Page "Mon compte" avec gestion de l'abonnement

Utilise Stripe Checkout pour simplifier l'intégration.`} title="Prompt intégration Stripe" />

                <div className="tip-box">
                  <div className="tip-icon">💡</div>
                  <div className="tip-content">
                    <h4>Le secret : nichéz-vous</h4>
                    <p>Ne vendez pas "des sites web". Vendez "des sites pour les coachs sportifs".
                       Plus c'est spécifique, plus c'est facile à vendre et plus vous pouvez facturer cher.</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== SECTION 12: FAQ ==================== */}
          <div className="track-section">
            <div className="section-toggle" onClick={() => toggleSection('faq')}>
              <div className="section-toggle-content">
                <span className="section-number">12</span>
                <div>
                  <h2>Questions fréquentes (FAQ)</h2>
                  <p>Toutes les réponses à vos questions sur Claude Code</p>
                </div>
              </div>
              <span className="section-arrow">{expandedSections.faq ? '▼' : '▶'}</span>
            </div>

            {expandedSections.faq && (
              <div className="section-content">
                <div className="section-header-with-checkbox">
                  <h3>Les questions les plus posées</h3>
                  <SectionCheckbox moduleId={MODULE_ID} sectionId="faq" />
                </div>

                <div className="faq-list">
                  <div className="faq-item">
                    <div className="faq-q">❓ Est-ce que j'ai besoin de savoir coder ?</div>
                    <div className="faq-a">
                      <strong>Non !</strong> C'est justement le but de Claude Code. Tu décris ce que tu veux en français,
                      et il code pour toi. Cela dit, comprendre les bases (HTML, CSS) peut aider à mieux communiquer.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ Claude Code peut-il créer n'importe quelle application ?</div>
                    <div className="faq-a">
                      Il peut créer la plupart des applications web : sites, dashboards, SaaS, outils métier, etc.
                      Pour les apps mobiles natives (iOS/Android), il peut créer du React Native.
                      Les très gros projets complexes nécessitent plus de guidance.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ Mes données sont-elles en sécurité ?</div>
                    <div className="faq-a">
                      Claude Code lit les fichiers de ton projet <strong>localement</strong> sur ton ordinateur.
                      Les conversations sont envoyées aux serveurs Anthropic de manière sécurisée.
                      Ne mets jamais de mots de passe ou clés API dans tes fichiers.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ Que faire si Claude Code fait une erreur ?</div>
                    <div className="faq-a">
                      C'est normal ! Dis-lui simplement : <strong>"Ça ne marche pas, j'ai cette erreur : [erreur]"</strong>.
                      Il va corriger. Tu peux aussi utiliser Git pour revenir en arrière si nécessaire.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ Puis-je utiliser Claude Code pour des projets professionnels ?</div>
                    <div className="faq-a">
                      <strong>Oui !</strong> De nombreux développeurs et entreprises l'utilisent en production.
                      Le code généré t'appartient, tu peux le vendre ou l'utiliser commercialement.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ Comment Claude Code est différent de Copilot ou Cursor ?</div>
                    <div className="faq-a">
                      <strong>Copilot</strong> : Complète ton code ligne par ligne (tu dois coder).<br/>
                      <strong>Cursor</strong> : Éditeur de code avec IA intégrée (tu dois coder).<br/>
                      <strong>Claude Code</strong> : Tu décris, il crée tout le projet. Pas besoin de coder.
                    </div>
                  </div>

                  <div className="faq-item">
                    <div className="faq-q">❓ Que faire si j'ai une question ou un problème ?</div>
                    <div className="faq-a">
                      Rejoins notre communauté <strong>Telegram F12IA™</strong> ! Des membres et formateurs peuvent t'aider.
                      Tu peux aussi demander directement à Claude Code d'expliquer ou de corriger.
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ==================== QUIZ ==================== */}
          <section className="module-section">
            <h2>📝 Quiz de validation</h2>
            <p>Testez vos connaissances sur Claude Code. Score minimum : 70%</p>
            <Quiz moduleId={MODULE_ID} questions={quizQuestions} />
          </section>

          {/* ==================== RÉSUMÉ ==================== */}
          <section className="module-section">
            <h2>📋 Ce que tu as appris dans cette formation</h2>
            <div className="summary-box">
              <ul>
                <li><strong>C'est quoi Claude Code</strong> — Un développeur IA qui crée du code sur ton ordi</li>
                <li><strong>Installation</strong> — Node.js + npm install + claude login</li>
                <li><strong>Utilisation</strong> — Parle naturellement, il code pour toi</li>
                <li><strong>Super-pouvoirs</strong> — Fichiers, commandes, debug, vision globale</li>
                <li><strong>Tarifs</strong> — Gratuit pour tester, 20$/mois pour un usage régulier</li>
                <li><strong>Créer des projets</strong> — Portfolio, apps, plateformes complètes</li>
                <li><strong>L'art du brief</strong> — Contexte, fonctionnalités, parcours, design</li>
                <li><strong>Déploiement</strong> — Vercel + Supabase + Stripe = stack gratuite</li>
                <li><strong>Monétisation</strong> — Sites à 500-2000€, SaaS à revenus récurrents</li>
              </ul>
            </div>

            <div className="tip-box" style={{marginTop: '24px', background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.05) 100%)', border: '2px solid #8b5cf6'}}>
              <div className="tip-icon">🚀</div>
              <div className="tip-content">
                <h4>Tu es maintenant prêt !</h4>
                <p>
                  Tu as toutes les connaissances pour créer des sites web et applications avec Claude Code.<br/>
                  <strong>La prochaine étape ?</strong> Lance-toi ! Crée ton premier projet et rejoins notre communauté Telegram pour partager tes créations !
                </p>
              </div>
            </div>
          </section>

          {/* Telegram */}
          <TelegramWidget />

          {/* Navigation */}
          <div className="module-navigation">
            <button className="btn btn-secondary" onClick={() => navigate('/formation/chatgpt')}>← Parcours ChatGPT</button>
            <button className="btn btn-primary" onClick={() => navigate('/certificate')}>Obtenir mon certificat →</button>
          </div>

        </div>
      </main>
    </div>
  );
};

export default ClaudeCodeTrack;
